import { useState, useEffect, useCallback } from "react";
import { getCurrentNoise } from "@/utils/noiseMatrix";
import { NoiseMatrix } from "@/utils/noiseMatrix";
import AdminAuthGate from "@/components/AdminAuthGate";
import { useNavigate } from "react-router-dom";
import { Star, Plus, RefreshCw, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import SpotEditModal, { type SpotEditData } from "@/components/spots-admin/SpotEditModal";
import SpotDeleteDialog from "@/components/spots-admin/SpotDeleteDialog";

const API_BASE = "https://spotsvc.up.railway.app";

interface SpotItem {
  id: string;
  name: string;
  short_address: string | null;
  category: string;
  access_type: string;
  wifi_available: boolean;
  power_outlets: boolean;
  noise_level: string | null;
  noise_matrix: NoiseMatrix | null;
  rating: number | null;
  is_active: boolean;
  photos: string[];
  created_at: string;
  description?: string | null;
  admin_notes?: string | null;
}

interface SpotsResponse {
  spots: SpotItem[];
  total: number;
  page: number;
  page_size: number;
}

const categoryLabels: Record<string, string> = {
  cafe: "Cafe", gym: "Gym", hotel_lobby: "Hotel Lobby",
  coworking: "Coworking", library: "Library", restaurant: "Restaurant",
  pod: "Pod", other: "Other",
};
const accessLabels: Record<string, string> = {
  free: "Free", purchase_required: "Purchase Required", members_only: "Members Only",
};

function SkeletonCard() {
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden animate-pulse">
      <div className="h-40 bg-muted" />
      <div className="p-4 space-y-3">
        <div className="h-5 bg-muted rounded w-3/4" />
        <div className="h-4 bg-muted rounded w-1/2" />
        <div className="flex gap-2">
          <div className="h-5 bg-muted rounded-full w-14" />
          <div className="h-5 bg-muted rounded-full w-20" />
        </div>
      </div>
    </div>
  );
}

function SpotCard({
  spot,
  onEdit,
  onDelete,
}: {
  spot: SpotItem;
  onEdit: (spot: SpotItem) => void;
  onDelete: (spot: SpotItem) => void;
}) {
  const photoUrl = spot.photos?.[0];

  return (
    <div
      className={`rounded-xl border border-border bg-card overflow-hidden hover:shadow-md transition-shadow relative ${
        !spot.is_active ? "opacity-60" : ""
      }`}
    >
      {/* Action buttons */}
      <div className="absolute top-2 right-2 z-10 flex gap-1">
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(spot); }}
          className="p-1.5 rounded-md bg-card/80 backdrop-blur-sm text-muted-foreground hover:text-foreground transition-colors"
          title="Edit"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(spot); }}
          className="p-1.5 rounded-md bg-card/80 backdrop-blur-sm text-muted-foreground hover:text-destructive transition-colors"
          title="Delete"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Inactive badge */}
      {!spot.is_active && (
        <div className="absolute top-2 left-2 z-10 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
          Inactive
        </div>
      )}

      {photoUrl ? (
        <img src={photoUrl} alt={spot.name} className="w-full h-40 object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-40 bg-muted flex items-center justify-center">
          <span className="text-muted-foreground text-sm">No photo</span>
        </div>
      )}
      <div className="p-4 space-y-2">
        <h3 className="font-bold text-foreground truncate">{spot.name}</h3>
        <p className="text-sm text-muted-foreground truncate">{spot.short_address || "No address"}</p>

        <div className="flex flex-wrap gap-1.5">
          <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
            {categoryLabels[spot.category] || spot.category}
          </span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
            {accessLabels[spot.access_type] || spot.access_type}
          </span>
          {(() => {
            const noise = getCurrentNoise(spot.noise_matrix);
            return noise ? (
              <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                {noise.label}
              </span>
            ) : null;
          })()}
        </div>

        <div className="flex items-center justify-between pt-1">
          {spot.rating != null ? (
            <div className="flex items-center gap-1 text-sm text-foreground">
              <Star className="w-3.5 h-3.5 fill-current text-amber-500" />
              <span>{spot.rating}</span>
            </div>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className={`w-2 h-2 rounded-full ${spot.is_active ? "bg-green-500" : "bg-gray-400"}`} />
            {spot.is_active ? "Active" : "Inactive"}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SpotsManagementProtected() {
  return (
    <AdminAuthGate>
      <SpotsManagementInner />
    </AdminAuthGate>
  );
}

function SpotsManagementInner() {
  const navigate = useNavigate();
  
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<SpotsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Modal/dialog state
  const [editingSpot, setEditingSpot] = useState<SpotItem | null>(null);
  const [deletingSpot, setDeletingSpot] = useState<SpotItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchSpots = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: "3" });
      if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
      const res = await fetch(`${API_BASE}/admin/spots?${params}`);
      if (!res.ok) throw new Error();
      const json: SpotsResponse = await res.json();
      setData(json);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => { fetchSpots(); }, [fetchSpots]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ---- Delete ----
  const handleDeleteConfirm = async () => {
    if (!deletingSpot || !data) return;
    setDeleteLoading(true);
    const removedSpot = deletingSpot;
    // Optimistic remove
    setData((prev) =>
      prev ? { ...prev, spots: prev.spots.filter((s) => s.id !== removedSpot.id), total: prev.total - 1 } : prev
    );
    setDeletingSpot(null);

    try {
      const res = await fetch(`${API_BASE}/admin/spots/${removedSpot.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      toast.success(`${removedSpot.name} has been removed.`);
    } catch {
      // Rollback
      setData((prev) =>
        prev ? { ...prev, spots: [...prev.spots, removedSpot], total: prev.total + 1 } : prev
      );
      toast.error("Failed to delete spot. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  // ---- Edit / Save ----
  const handleEditSave = async (id: string, changes: Partial<SpotEditData>) => {
    const res = await fetch(`${API_BASE}/admin/spots/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(changes),
    });
    if (!res.ok) throw new Error();
    const updated = await res.json();
    // Update in list
    setData((prev) =>
      prev ? { ...prev, spots: prev.spots.map((s) => (s.id === id ? { ...s, ...updated } : s)) } : prev
    );
    setEditingSpot(null);
    toast.success(`${updated.name} has been saved.`);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-foreground">Spots</h1>
          <Button onClick={() => navigate("/spots/add")} size="sm">
            <Plus className="w-4 h-4" />
            Add Spot
          </Button>
        </div>

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name…"
          className="w-full px-4 py-2.5 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />

        {error && (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <p className="text-muted-foreground">Failed to load spots.</p>
            <Button variant="outline" onClick={fetchSpots}><RefreshCw className="w-4 h-4" />Retry</Button>
          </div>
        )}

        {loading && !error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        )}

        {!loading && !error && data && data.spots.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <p className="text-muted-foreground">No spots added yet</p>
            <Button onClick={() => navigate("/spots/add")}><Plus className="w-4 h-4" />Add your first spot</Button>
          </div>
        )}

        {!loading && !error && data && data.spots.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.spots.map((spot) => (
                <SpotCard key={spot.id} spot={spot} onEdit={setEditingSpot} onDelete={setDeletingSpot} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 pt-4">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => handlePageChange(page - 1)}>Previous</Button>
                <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => handlePageChange(page + 1)}>Next</Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Modal */}
      {editingSpot && (
        <SpotEditModal spot={editingSpot} onClose={() => setEditingSpot(null)} onSave={handleEditSave} />
      )}

      {/* Delete Dialog */}
      {deletingSpot && (
        <SpotDeleteDialog
          spotName={deletingSpot.name}
          onCancel={() => setDeletingSpot(null)}
          onConfirm={handleDeleteConfirm}
          deleting={deleteLoading}
        />
      )}
    </div>
  );
}
